
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { images } from '../assets/images';
import { ArrowRight, Plus, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleItems, setVisibleItems] = useState(6);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const portfolioRef = useRef<HTMLDivElement>(null);
  const categories = ['all', 'residential', 'commercial'];
  
  const filteredProjects = activeCategory === 'all' 
    ? images.portfolio
    : images.portfolio.filter(project => 
        project.category.toLowerCase() === activeCategory.toLowerCase()
      );

  useEffect(() => {
    const observeElements = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-fade-up', 'opacity-100');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px',
        }
      );

      const elements = portfolioRef.current?.querySelectorAll('.portfolio-item');
      elements?.forEach((el) => {
        el.classList.add('opacity-0');
        observer.observe(el);
      });

      return observer;
    };

    const observer = observeElements();
    
    return () => {
      observer.disconnect();
    };
  }, [activeCategory]);

  const handleImageError = (src: string) => {
    console.error(`Failed to load portfolio image: ${src}`);
    setImageErrors(prev => ({...prev, [src]: true}));
  };

  return (
    <section ref={portfolioRef} className="section-padding">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 opacity-0 animate-fade-up">
          <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block">
            Our Work
          </span>
          <h2 className="section-title">Featured Projects</h2>
          <p className="max-w-3xl mx-auto text-pkstone-700 text-lg">
            Explore our portfolio of landscape transformations throughout Fairfield County. 
            Each project reflects our commitment to quality, creativity, and client satisfaction.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex justify-center mb-12 opacity-0 animate-fade-up">
          <div className="inline-flex bg-pkstone-100 rounded-lg p-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  "px-6 py-2 rounded-md font-medium transition-all duration-300 ease-smooth capitalize",
                  activeCategory === category
                    ? "bg-white shadow-md text-pkgreen-600"
                    : "text-pkstone-700 hover:text-pkgreen-600"
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredProjects.slice(0, visibleItems).map((project) => (
            <div
              key={project.id}
              className="portfolio-item group rounded-lg overflow-hidden shadow-md bg-white"
            >
              <div className="relative overflow-hidden h-64 md:h-72">
                {imageErrors[project.image] ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <div className="text-center p-4">
                      <AlertCircle className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Image unavailable</p>
                    </div>
                  </div>
                ) : (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-105"
                    onError={() => handleImageError(project.image)}
                  />
                )}
                <div className="absolute inset-0 bg-pkgreen-950/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Link 
                    to={`/portfolio/${project.id}`}
                    className="bg-white/90 backdrop-blur-sm text-pkgreen-800 p-3 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300 ease-smooth"
                  >
                    <Plus className="h-6 w-6" />
                  </Link>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-pkstone-800 text-sm px-3 py-1 rounded-full font-medium">
                    {project.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold mb-2">{project.title}</h3>
                <p className="text-pkstone-600 mb-3">{project.location}</p>
                <Link
                  to={`/portfolio/${project.id}`}
                  className="inline-flex items-center text-pkgreen-600 font-medium hover:text-pkgreen-700 transition-colors"
                >
                  View project
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-16 opacity-0 animate-fade-up">
          <Link
            to="/portfolio"
            className="bg-pkearth-100 hover:bg-pkearth-200 text-pkearth-900 font-medium py-3 px-8 rounded-md inline-flex items-center transition-all duration-300 ease-smooth"
          >
            View Full Portfolio
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
